

import { supabase } from '@/integrations/supabase/client';
import { Database } from '@/integrations/supabase/types';

type EventRegistrationsInsert = Database['public']['Tables']['event_registrations']['Insert'];

export const registerForEvent = async (eventId: string, userId: string): Promise<boolean> => {
  try {
    const { data, error } = await supabase
      .from('event_registrations')
      .insert({
        event_id: eventId,
        user_id: userId,
        status: 'confirmed'
      } as EventRegistrationsInsert)
      .select('id')
      .single();

    if (error) {
      console.error('Error registering for event:', error);
      throw error;
    }

    // 📧 Envoyer email de confirmation (non-bloquant)
    if (data?.id) {
      supabase.functions.invoke('email-event-registration', {
        body: {
          userId,
          eventId,
          registrationId: data.id
        }
      }).then(res => {
        if (res.error) {
          console.warn('Failed to send event registration email:', res.error);
        }
      }).catch(err => {
        console.warn('Email function error:', err);
      });
    }

    return true;
  } catch (error) {
    console.error('Error in registerForEvent:', error);
    return false;
  }
};

export const unregisterFromEvent = async (eventId: string, userId: string): Promise<boolean> => {
  try {
    const { error } = await supabase
      .from('event_registrations')
      .delete()
      .match({ event_id: eventId, user_id: userId });

    if (error) {
      console.error('Error unregistering from event:', error);
      throw error;
    }

    return true;
  } catch (error) {
    console.error('Error in unregisterFromEvent:', error);
    return false;
  }
};

export const getRegisteredEvents = async (userId: string): Promise<string[]> => {
  try {
    const { data, error } = await supabase
      .from('event_registrations')
      .select('event_id')
      .eq('user_id', userId);

    if (error) {
      console.error('Error fetching registered events:', error);
      throw error;
    }

    return data ? data.map((reg) => reg.event_id) : [];
  } catch (error) {
    console.error('Error in getRegisteredEvents:', error);
    return [];
  }
};

// ========== Admin helpers ==========
export type EventRegistration = {
  id: string;
  event_id: string;
  user_id: string;
  created_at: string;
  status: string;
};

export const getRegistrationsForEvent = async (eventId: string): Promise<EventRegistration[]> => {
  try {
    const { data, error } = await supabase
      .from('event_registrations')
      .select('id, event_id, user_id, created_at, status')
      .eq('event_id', eventId);

    if (error) throw error;
    return (data as EventRegistration[]) || [];
  } catch (error) {
    console.error('Error in getRegistrationsForEvent:', error);
    return [];
  }
};

export const deleteRegistrationById = async (registrationId: string): Promise<boolean> => {
  try {
    const { error } = await supabase
      .from('event_registrations')
      .delete()
      .eq('id', registrationId);

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error in deleteRegistrationById:', error);
    return false;
  }
};

export const getAttendeeCountsForEvents = async (eventIds: string[]): Promise<Record<string, number>> => {
  if (eventIds.length === 0) return {};
  try {
    const { data, error } = await supabase
      .from('event_registrations')
      .select('event_id')
      .in('event_id', eventIds);

    if (error) throw error;

    const counts: Record<string, number> = {};
    (data || []).forEach((row: { event_id: string }) => {
      counts[row.event_id] = (counts[row.event_id] || 0) + 1;
    });
    return counts;
  } catch (error) {
    console.error('Error in getAttendeeCountsForEvents:', error);
    return {};
  }
};
